<script setup lang="ts">
/**
 * 〈市集 › 擴充〉（`/plugins`；批153 批A）——admin 以上。
 *
 * 市集登記的全部 plugin：頁籤「全部／公開／私人」、產品篩選、keyset「載入更多」。
 * 〈＋ 登記擴充〉一律私人、沒有版本（dl 的 channel 在第一次上傳時才建）；之後到詳情頁上傳版本、
 * 指派權利、上架（上架要營運主管）。規格：`license-server/docs/PLUGIN_MARKET.md`。
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import FilterClearButton from '@/components/FilterClearButton.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as pm from '@/api/pluginMarket'
import type { MarketPlugin, Publisher } from '@/api/pluginMarket'
import { vEnter } from '@/lib/enterKey'
import {
  DL_SYNC_LABELS,
  dlSyncState,
  labelOf,
  PLUGIN_KEY_RE,
  PLUGIN_STATUS_LABELS,
  sortOrderError,
  VISIBILITY_LABELS,
} from '@/lib/pluginMarket'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { fmtSeen } from '@/lib/staleness'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const store = useDataStore()
const dialogs = useDialogsStore()
const router = useRouter()

type Tab = '' | 'public' | 'private'
const TABS: { key: Tab; label: string }[] = [
  { key: '', label: '全部' },
  { key: 'public', label: '公開' },
  { key: 'private', label: '私人' },
]
// 批186：產品下拉與可見度籤記住；下面的 `watch` 會重載，清除只要改值。
const pf = usePersistedFilters('plugins', { product: '', tab: '' as Tab }, { oneOf: { tab: TABS.map((t) => t.key) } })
const { product, tab } = pf.refs
const { dirty: filtersDirty } = pf
const clearFilters = () => pf.clear()

const rows = ref<MarketPlugin[]>([])
const cursor = ref<string | null>(null)
const loading = ref(false)
const loadError = ref('')
const publishers = ref<Publisher[]>([])

function detailOf(e: unknown): unknown {
  return e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
}
function errText(e: unknown, fallback: string): string {
  const d = detailOf(e)
  if (typeof d === 'string') return d
  if (d && typeof d === 'object' && typeof (d as { message?: unknown }).message === 'string') {
    return (d as { message: string }).message
  }
  return fallback
}

/**
 * 請求序號（R3-4）：換條件時舊條件的慢回應一律丟掉——不然快速切頁籤會把「公開」的結果畫在「私人」底下，
 * 或拿舊游標配新條件去要下一頁。
 */
let seq = 0
async function load(reset: boolean) {
  if (!reset && (loading.value || !cursor.value)) return
  const my = ++seq
  loading.value = true
  try {
    const page = await pm.listPlugins({
      visibility: tab.value || undefined,
      product: product.value || undefined,
      cursor: reset ? null : cursor.value,
      limit: 50,
    })
    if (my !== seq) return
    rows.value = reset ? page.items : [...rows.value, ...page.items]
    cursor.value = page.next_cursor
    loadError.value = ''
  } catch (e) {
    if (my !== seq) return
    loadError.value = errText(e, '讀取擴充清單失敗')
  } finally {
    if (my === seq) loading.value = false
  }
}
watch([tab, product], () => {
  // 換條件：清空清單與游標，再讀第一頁。
  rows.value = []
  cursor.value = null
  void load(true)
})

onMounted(async () => {
  void store.ensureProducts().catch(() => {})
  void pm
    .listPublishers()
    .then((r) => (publishers.value = r.items))
    .catch(() => {})
  await load(true)
})

/** 開放 plugin 市集的產品（登記時只能選這些；篩選下拉列全部產品）。 */
const marketProducts = computed(() => store.products.filter((p) => p.options?.supports_plugin_market === true))

function openDetail(p: MarketPlugin) {
  void router.push({ name: 'plugin-detail', params: { id: p.id } })
}

// ---------------------------------------------------------------------------
// 登記
// ---------------------------------------------------------------------------
const createOpen = ref(false)
const createBusy = ref(false)
const createError = ref('')
const form = ref({ publisherId: '', key: '', product: '', name: '', summary: '', homepage: '', sortOrder: '100' })
function openCreate() {
  const firstParty = publishers.value.find((p) => p.first_party)
  form.value = {
    publisherId: firstParty?.id ?? '',
    key: '',
    product: marketProducts.value[0]?.key ?? '',
    name: '',
    summary: '',
    homepage: '',
    sortOrder: '100',
  }
  createError.value = ''
  createOpen.value = true
}
const selectedPublisher = computed(() => publishers.value.find((p) => p.id === form.value.publisherId) ?? null)
const createInvalid = computed(() => {
  const f = form.value
  const key = f.key.trim()
  if (!PLUGIN_KEY_RE.test(key)) return '擴充代號只能用小寫英數與底線、英文字母開頭、最多 32 字'
  const prefix = selectedPublisher.value?.key_prefix
  if (prefix && (!key.startsWith(prefix) || key.length <= prefix.length)) return `這個發行者的 key 必須以「${prefix}」開頭`
  if (!f.product) return '請選擇產品（產品要先開放擴充市集）'
  if (!f.name.trim()) return '名稱不可空白'
  if (f.homepage.trim() && !/^https:\/\//.test(f.homepage.trim())) return '首頁網址只收 https://'
  // R3-18：空字串不可以當成 0（`Number('')` 是 0，會排到最前面）。
  return sortOrderError(f.sortOrder)
})
async function doCreate() {
  if (createInvalid.value) return
  const f = form.value
  createBusy.value = true
  createError.value = ''
  try {
    const row = await pm.createPlugin({
      publisher_id: f.publisherId || null,
      key: f.key.trim(),
      product: f.product,
      name: f.name.trim(),
      summary: f.summary.trim(),
      homepage_url: f.homepage.trim() || null,
      sort_order: Number(f.sortOrder.trim()),
    })
    createOpen.value = false
    void router.push({ name: 'plugin-detail', params: { id: row.id } })
  } catch (e) {
    createError.value = errText(e, '登記擴充失敗')
  } finally {
    createBusy.value = false
  }
}

async function showSync() {
  try {
    const s = await pm.getSync()
    await dialogs.notify({
      title: '市集同步狀態',
      message:
        `命名空間：${s.ns ?? '（未啟用）'}・市集金鑰：${s.key_available ? s.market_kid : '不在'}\n` +
        `feed 上次推送：${fmtSeen(s.feed.pushed_at)}` +
        (s.feed.last_error ? `\n最近錯誤：${s.feed.last_error}` : '') +
        `\n待處理：卡住的上傳 ${s.pending.stuck_uploads}・待刪除的撤回版本 ${s.pending.withdraw_deletes}` +
        `・核准處理中 ${s.pending.approvals ?? 0}・存取不一致 ${s.pending.access_drift}`,
    })
  } catch (e) {
    await dialogs.notify({ title: '讀取失敗', message: errText(e, '讀取同步狀態失敗') })
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>擴充</h1>
      <span class="count">{{ rows.length }}{{ cursor ? '+' : '' }}</span>
      <span class="spacer" />
      <select v-model="product" aria-label="產品">
        <option value="">全部產品</option>
        <option v-for="p in store.products" :key="p.key" :value="p.key">{{ p.name }}</option>
      </select>
      <FilterClearButton :dirty="filtersDirty" @click="clearFilters" />
      <UiButton variant="secondary" @click="showSync">同步狀態</UiButton>
      <UiButton variant="primary" @click="openCreate">＋ 登記擴充</UiButton>
    </div>

    <p class="subtle intro">
      市集登記的擴充。<strong>私人</strong>的只有發行者自己的組織與被指派了權利的客戶看得到；
      <strong>公開</strong>的任何站台都能在市集看到、匿名下載。
    </p>

    <div class="tabs" data-hscroll>
      <button
        v-for="t in TABS"
        :key="t.key"
        type="button"
        class="tab"
        :class="{ active: tab === t.key }"
        @click="tab = t.key"
      >
        {{ t.label }}
      </button>
    </div>

    <p v-if="loadError" class="err">{{ loadError }}</p>
    <div class="card">
      <div class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr>
              <th>擴充</th><th>產品</th><th>發行者</th><th>可見性</th><th>最新版</th><th>發布主機</th><th class="col-state">狀態</th><th>登記</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in rows" :key="p.id" class="clickable" @click="openDetail(p)">
              <td>
                <div class="pname">{{ p.name }}</div>
                <code class="mono sub">{{ p.key }}</code>
                <!-- 窄螢幕：狀態欄在右半邊、會被擠進橫向捲動區——改成跟在 key 後面（寬螢幕隱藏；ui.css）。 -->
                <span class="status-pill state-inline" :class="p.status === 'active' ? 'active' : 'revoked'">
                  {{ labelOf(PLUGIN_STATUS_LABELS, p.status) }}
                </span>
              </td>
              <td class="nowrap">{{ store.productName(p.product) }}</td>
              <td>
                {{ p.publisher_name ?? '—' }}
                <span v-if="p.first_party" class="chip">第一方</span>
              </td>
              <td class="nowrap">
                <span class="status-pill" :class="p.visibility === 'public' ? 'active' : 'superseded'">
                  {{ labelOf(VISIBILITY_LABELS, p.visibility) }}
                </span>
              </td>
              <td class="nowrap"><code class="mono">{{ p.latest_version ?? '—' }}</code></td>
              <td class="nowrap">
                <span :class="['sync', dlSyncState(p)]">{{ DL_SYNC_LABELS[dlSyncState(p)] }}</span>
              </td>
              <td class="nowrap col-state">
                <span class="status-pill" :class="p.status === 'active' ? 'active' : 'revoked'">
                  {{ labelOf(PLUGIN_STATUS_LABELS, p.status) }}
                </span>
              </td>
              <td class="nowrap subtle">{{ fmtSeen(p.created_at, { short: true }) }}</td>
            </tr>
            <tr v-if="!loading && !rows.length">
              <td colspan="8" class="subtle empty-row">
                {{ tab || product ? '沒有符合條件的擴充。' : '還沒有登記任何擴充。' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="more-row">
        <span v-if="loading" class="subtle"><span class="spinner" /> 載入中…</span>
        <UiButton v-else-if="cursor" size="sm" @click="load(false)">載入更多</UiButton>
      </div>
    </div>

    <UiModal :open="createOpen" title="登記擴充" width="640px" :close-on-backdrop="false" @close="createOpen = false">
      <p class="subtle">
        登記後一律是<strong>私人</strong>、沒有任何版本；到詳情頁上傳 <code>.ipoplug</code>、指派客戶或上架。
        擴充代號建立後不可更改，而且必須與套件 manifest 的 <code>key</code> 相同。
      </p>
      <p v-if="createError" class="err">{{ createError }}</p>
      <div class="form-grid two">
        <label class="field">
          <span class="field-label">發行者</span>
          <select v-model="form.publisherId">
            <option v-for="p in publishers" :key="p.id" :value="p.id">
              {{ p.display_name }}{{ p.first_party ? '（第一方）' : p.key_prefix ? `（${p.key_prefix}）` : '' }}
            </option>
          </select>
        </label>
        <label class="field">
          <span class="field-label">產品</span>
          <select v-model="form.product">
            <option v-for="p in marketProducts" :key="p.key" :value="p.key">{{ p.name }}</option>
          </select>
          <small v-if="!marketProducts.length" class="err">
            還沒有任何產品開放擴充市集（到〈產品〉頁的憑證設計勾「開放擴充市集」）。
          </small>
        </label>
        <label class="field">
          <span class="field-label">擴充代號</span>
          <input
            v-model="form.key"
            class="mono"
            :placeholder="selectedPublisher?.key_prefix ? `${selectedPublisher.key_prefix}…` : '例：my_plugin'"
            maxlength="32"
            v-enter="doCreate"
          />
        </label>
        <label class="field">
          <span class="field-label">名稱</span>
          <input v-model="form.name" maxlength="60" v-enter="doCreate" />
        </label>
      </div>
      <label class="field block">
        <span class="field-label">一句話說明（選填，≤160 字）</span>
        <input v-model="form.summary" maxlength="160" />
      </label>
      <div class="form-grid two block">
        <label class="field">
          <span class="field-label">首頁網址（選填）</span>
          <input v-model="form.homepage" type="url" placeholder="https://…" maxlength="500" />
        </label>
        <label class="field">
          <span class="field-label">市集排序（小的在前）</span>
          <input v-model="form.sortOrder" inputmode="numeric" />
        </label>
      </div>
      <p v-if="createInvalid" class="subtle">{{ createInvalid }}</p>
      <template #foot>
        <UiButton @click="createOpen = false">取消</UiButton>
        <UiButton variant="primary" :disabled="!!createInvalid" :loading="createBusy" @click="doCreate">登記</UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.intro {
  margin: 0 0 var(--sp-3);
}
.tabs {
  margin-bottom: var(--sp-3);
}
.err {
  margin: var(--sp-2) 0;
  color: var(--danger);
  font-size: var(--fs-12);
}
.clickable {
  cursor: pointer;
}
.clickable:hover td {
  background: var(--bg-hover);
}
.pname {
  /* min-width：窄螢幕時別把名稱擠成好幾行（表格在 .tbl-scroll 裡可橫向捲動）。 */
  min-width: 160px;
  font-weight: 600;
  color: var(--fg);
}
.mono {
  font-family: var(--font-mono);
}
.sub {
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.nowrap {
  white-space: nowrap;
}
.chip {
  margin-left: 4px;
}
.sync {
  font-size: var(--fs-12);
}
.sync.synced {
  color: var(--success);
}
.sync.pending {
  color: var(--warning);
}
.sync.error {
  color: var(--danger);
}
.sync.none {
  color: var(--fg-4);
}
.empty-row {
  text-align: center;
  padding: var(--sp-5) 0;
}
.more-row {
  display: flex;
  justify-content: center;
  margin-top: var(--sp-3);
}
.form-grid.two {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}
.block {
  margin-top: var(--sp-3);
}
</style>
